// Mineral Intelligence — Inbox API client wrappers.
//
// Browser-side surface for the Monday weekly flow:
//   - recorderIngest(file, foundation): POST a single CSV to
//     /api/mi/ingest/recorder. Returns the per-file diff summary
//     (rows_in_csv, docs_created, parties_created, unhandled_doc_types).
//   - poolOrderIngest(formData): POST multipart to
//     /api/mi/ingest/pool-order. Same shape the existing
//     UploadPoolOrderModal uses; thin wrapper exposed here so
//     Inbox doesn't reach into the pool-orders services module.
//   - refreshCoverage(): POST /api/mi/admin/refresh-coverage.
//     Auto-called after the last file in each Inbox batch.
//   - recent(): GET /api/mi/inbox/recent. Returns the last 10
//     ingests for the bottom-of-page log.
//
// Recorder uploads stream the CSV body as text/csv with the
// X-Filename header. Foundation mode adds X-Foundation-Load: true;
// weekly mode omits it. Weekly is the default; foundation requires
// the typed FOUNDATION confirmation in the UI.

import { authHeaders } from '../../lib/authHeaders';

export interface RecorderIngestSummary {
  rows_in_csv: number;
  rows_skipped: number;
  duplicate_doc_numbers_in_csv: number;
  rows_processed: number;
  docs_created: number;
  docs_already_present: number;
  parties_created: number;
  parties_matched_existing: number;
  aliases_seen: number;
  trs_links_created: number;
  unparseable_trs_rows: number;
  unhandled_doc_types: Array<{ doc_type: string; count: number; sample_doc: string }>;
}

export interface RecorderIngestResponse {
  batch_id: string;
  status: 'complete';
  foundation_load: boolean;
  summary: RecorderIngestSummary;
}

export interface RefreshCoverageResponse {
  ok: true;
  elapsed_ms: number;
  trs_count: number;
  total_doc_links: number;
  earliest_recording_date: string | null;
  latest_recording_date: string | null;
}

export interface InboxRecentRow {
  kind: 'recorder' | 'pipeline' | 'pool_order';
  id: string;
  created_at: string;
  badge: string;
  label: string;
  detail: string;
  link: string | null;
}

export interface PoolOrderIngestResponse {
  pool_order_id: string;
  docket_number: string | null;
  parse_status: 'complete' | 'failed';
  parse_warnings: string[];
  extracted?: Record<string, unknown>;
  summary?: Record<string, unknown>;
}

async function jsonErr(r: Response): Promise<never> {
  let detail = r.statusText;
  try { detail = (await r.json()).error ?? detail; } catch {}
  throw new Error(`${r.status} ${detail}`);
}

export const inboxApi = {
  // ── Recorder CSV ingest ────────────────────────────────────────
  // Streams the file as text/csv with X-Filename. foundation=true
  // sets X-Foundation-Load: true so the docs land as historical
  // backfill (excluded from this-week stats). Default false = weekly
  // ingest (counts as real new activity).
  recorderIngest: async (file: File, foundation: boolean): Promise<RecorderIngestResponse> => {
    const headers: Record<string, string> = {
      ...authHeaders(),
      'Content-Type': 'text/csv',
      'X-Filename': file.name,
    };
    if (foundation) headers['X-Foundation-Load'] = 'true';
    const r = await fetch('/api/mi/ingest/recorder', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: file,
    });
    if (!r.ok) return jsonErr(r);
    return r.json();
  },

  // ── Pool order PDF ingest (multipart) ──────────────────────────
  poolOrderIngest: async (form: FormData): Promise<PoolOrderIngestResponse> => {
    const r = await fetch('/api/mi/ingest/pool-order', {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: form,
    });
    if (!r.ok) return jsonErr(r);
    return r.json();
  },

  // ── Coverage refresh ───────────────────────────────────────────
  refreshCoverage: async (): Promise<RefreshCoverageResponse> => {
    const r = await fetch('/api/mi/admin/refresh-coverage', {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!r.ok) return jsonErr(r);
    return r.json();
  },

  // ── Recent ingests log ─────────────────────────────────────────
  recent: async (): Promise<{ recent: InboxRecentRow[] }> => {
    const r = await fetch('/api/mi/inbox/recent', {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!r.ok) return jsonErr(r);
    return r.json();
  },
};
